/**
 * Registro de herramientas del panel.
 *
 * Fuente única del catálogo de herramientas (el "enchufe" del patrón DLC). El
 * panel pinta las tarjetas desde aquí y el planning enlaza sus tareas a estas
 * herramientas. Añadir/quitar una herramienta se hace en esta lista.
 *
 * Ver docs/crear-herramienta.md.
 */

import type { ComponentType } from 'react';

export type EstadoHerr = 'listo' | 'proximo';
export type Fase = 'previa' | 'directo' | 'post';

export interface Herramienta {
  id: string;
  titulo: string;
  descripcion: string;
  estado: EstadoHerr;
  /** Momento de la cobertura en el que se usa (agrupa las tarjetas). */
  fase: Fase;
  /**
   * Ruta EXISTENTE a la que apunta (p. ej. una vista ya montada como
   * `/resultados`). Para herramientas que reutilizan una pantalla ya presente.
   */
  to?: string;
  /**
   * Carga diferida del componente propio de la herramienta (patrón DLC). Si se
   * define, la herramienta se sirve en `/h/<id>` SIN tocar App.tsx: basta con
   * añadir la entrada aquí. Ver src/tools/HerramientaHost.tsx y
   * docs/crear-herramienta.md.
   */
  cargar?: () => Promise<{ default: ComponentType }>;
  icono: string;
}

/** Fases de la cobertura, en orden. La etiqueta es el título de cada sección. */
export const FASES: { id: Fase; label: string }[] = [
  { id: 'previa', label: 'Previa' },
  { id: 'directo', label: 'Durante · en directo' },
  { id: 'post', label: 'Después' },
];

export const HERRAMIENTAS: Herramienta[] = [
  // ---- Previa ----
  {
    id: 'previa-articulo',
    titulo: 'Previa del campeonato',
    descripcion:
      'Artículo para la web con lo relevante a partir de inscritos, marcas a batir y favoritos. (Formato del artículo: a definir.)',
    estado: 'proximo',
    fase: 'previa',
    icono: '📰',
  },
  {
    id: 'previa-prueba',
    titulo: 'Previa por prueba',
    descripcion:
      'Marcas a batir, favoritos y duelos por prueba a partir de los inscritos, antes de que empiece.',
    estado: 'proximo',
    fase: 'previa',
    icono: '📋',
  },
  // ---- Durante · en directo ----
  {
    id: 'resultados',
    titulo: 'Publicaciones de resultados',
    descripcion:
      'Crea contenido a partir del enlace de la competición (la herramienta "barra atleta").',
    estado: 'listo',
    fase: 'directo',
    // Apunta a /resultados = vista AtletaEnVivo (la herramienta buena, "barra
    // atleta"). Antes iba a /inicio, que es la versión vieja con defectos.
    // TODO (anotado, pendiente de decidir): 3 tipos de contenido desde el mismo
    // enlace — (1) listados de salida, (2) top-3, (3) atleta seleccionado.
    // ¿3 herramientas o 3 modos dentro de una? Ver docs/herramienta-resultados.md.
    to: '/resultados',
    icono: '🥇',
  },
  {
    id: 'telegram',
    titulo: 'Publicación en Telegram',
    descripcion:
      'Series, clasificados (Q/q), concursos por rondas y combinadas al canal @mundoatletismo_live. Automático el día del campeonato; aquí el manual/respaldo.',
    estado: 'listo',
    fase: 'directo',
    // Vista construida sobre AtletaEnVivo en modo admin (antes /admin).
    to: '/telegram',
    icono: '📣',
  },
  {
    id: 'twitter',
    titulo: 'Publicaciones para Twitter/X',
    descripcion:
      'Redacta el tuit (con plantillas) y ábrelo en X con el texto ya puesto. Publicación por API: en preparación.',
    estado: 'listo',
    fase: 'directo',
    cargar: () => import('@/tools/twitter'),
    icono: 'X',
  },
  {
    id: 'articulos',
    titulo: 'Generador de artículos',
    descripcion:
      'Previa, post-jornada y post-campeonato con enfoque periodístico. Motor listo (ángulos + valor-noticia + redactor); en construcción el dato en vivo. Ver docs/guia-articulos*.md.',
    estado: 'listo',
    fase: 'directo',
    cargar: () => import('@/tools/previa'),
    icono: '📰',
  },
  {
    id: 'hitos',
    titulo: 'Detector de hitos',
    descripcion:
      'Avisa cuando una marca es récord, mínima internacional, líder nacional o top-10 all-time.',
    estado: 'proximo',
    fase: 'directo',
    icono: '🚨',
  },
  {
    id: 'buscador',
    titulo: 'Buscador de atleta',
    descripcion:
      'Histórico, mejores marcas y posición all-time de cualquier atleta para redactar al vuelo.',
    estado: 'proximo',
    fase: 'directo',
    icono: '🔎',
  },
  {
    id: 'speaker',
    titulo: 'Datos para speaker',
    descripcion:
      'Inscritos por prueba del Cto. (sub-20 y Absoluto) y dossier del comentarista: informe PDF por prueba o del campeonato completo (generación solo en local).',
    estado: 'listo',
    fase: 'directo',
    to: '/speaker',
    icono: '🎙️',
  },
  {
    id: 'cola',
    titulo: 'Cola de publicaciones',
    descripcion:
      'Coordina al equipo: pendiente → aprobado → publicado, para no duplicar tuits entre varios.',
    estado: 'proximo',
    fase: 'directo',
    icono: '🗂️',
  },
  // ---- Después ----
  {
    id: 'post-jornada',
    titulo: 'Post jornada',
    descripcion:
      'Artículo resumen de la jornada con los datos más relevantes. (Formato: a definir.)',
    estado: 'proximo',
    fase: 'post',
    icono: '📝',
  },
  {
    id: 'post-campeonato',
    titulo: 'Post campeonato',
    descripcion:
      'Artículo resumen del campeonato completo con lo más destacado. (Formato: a definir.)',
    estado: 'proximo',
    fase: 'post',
    icono: '🏁',
  },
];

/** Busca una herramienta por su id. `undefined` si no existe. */
export function getHerramienta(id: string): Herramienta | undefined {
  return HERRAMIENTAS.find((h) => h.id === id);
}

/**
 * Enlace al que lleva una herramienta:
 * - `to` si reutiliza una ruta existente,
 * - `/h/<id>` si trae su propio componente (`cargar`),
 * - `null` si no es navegable todavía (próximamente).
 */
export function enlaceHerramienta(h: Herramienta): string | null {
  if (h.to) return h.to;
  if (h.cargar) return `/h/${h.id}`;
  return null;
}
